import { BRAND_CONFIG } from '@@brand'
import { Product } from '@@types'
import { URL_PAGE_PRODUCT } from '@hive/platform-url-schema'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router'
import { ReactSVG } from 'react-svg'
import { useMediaQuery } from 'usehooks-ts'

import { formatPriceAmount } from '@entities/Product/helpers'

import ControlCart from '@features/cart/ui/control/CartControl'
import { FavoriteButton } from '@features/favorite'
import { InStock } from '@features/in-stock'

import { useIsAdult } from '@shared/hooks'
import { useAnalytics } from '@shared/lib/analytics'
import { cn } from '@shared/lib/cn'
import { LocalizedLink as Link } from '@shared/lib/locale'
import Badge from '@shared/ui/badge/Badge'
import BadgeGroup from '@shared/ui/badge/group'
import RatingPreview from '@shared/ui/rating/preview/preview'
import { formatPrice } from '@shared/uttils'

import styles from './styles.module.scss'

export interface ProductPreviewCardProps {
  data: Product;
  className?: string;
  onAddToCart?: () => void;
  /** Название списка для аналитики (каталог, поиск, рекомендации и т.д.) */
  listName?: string;
  /** ID списка для аналитики */
  listId?: string;
}

export default function ProductCard ({
  data,
  className,
  onAddToCart,
  listName,
  listId,
}: ProductPreviewCardProps) {
  const isTablet = useMediaQuery('(max-width: 767px)')
  const { isAdult } = useIsAdult()
  const { t: tf } = useTranslation('favorites')
  const { selectItem } = useAnalytics()

  const handleProductClick = () => {
    selectItem(data, listName, listId)
  }

  return (
    <div
      className={cn(styles.root, className, {
        [styles.blur]: !isAdult && data.is_alcohol,
      })}
    >
      <Link to={`/product/${data.slug}`} className={styles.root__top} onClick={handleProductClick}>
        {data.preview?.url ? (
          <img
            className={styles.root__img}
            src={data.preview?.url || ''}
            alt=""
            loading="lazy"
            fetchPriority="low"
          />
        ) : (
          <ReactSVG
            className={styles.root__plug}
            src={BRAND_CONFIG.images.productPlaceholder}
            wrapper="span"
          />
        )}

        { data.average_rating > 0 && (
          <RatingPreview
            className={styles.root__rating}
            value={data.average_rating || 0}
          /> 
        )}

        <FavoriteButton
          className={styles.root__favorite}
          id={data.id}
          isFavorite={!!data.isFavorite}
          product={data}
        />

        <BadgeGroup className={styles.root__badges}>
          {data.marketingLabels &&
            data.marketingLabels.map((item) => {
              return (
                <Badge 
                  key={item?.id}
                  backgroundColor={item?.optionBackgroundColor}
                  color={item.optionColor}
                  image={item.image?.url}
                />
              )
            })}

          {!isAdult && data.is_alcohol && (
            <Badge
              title={`${data.min_age ?? '21'}+`}
              backgroundColor={'#434242'}
            />
          )}
        </BadgeGroup>
      </Link>
      <div className={styles.root__bottom}>
        <Link
          className={styles['root__bottom-link']}
          to={generatePath(URL_PAGE_PRODUCT, { slug: data.slug })}
          onClick={handleProductClick}
        >
          <div className={styles.root__info}>
            {data.priceOffer && (
              <div
                className={cn(styles.root__price, {
                  [styles._accent]: !!data.priceOffer.oldPrice,
                })}
              >
                {formatPrice(data.priceOffer?.price ?? 0)}{' '}
                <span>/ {formatPriceAmount({ priceOffer: data.priceOffer, t: tf })}</span>{' '}
              </div>
            )}
            {/* <div className={styles.root__volume}>1 л</div> */}

            {!!data.priceOffer?.oldPrice && (
              <div className={styles['root__old-price']}>
                {formatPrice(data.priceOffer.oldPrice)}
              </div>
            )}
          </div>

          <h3 className={styles.root__name}>
            <span className={styles['root__name-inner']}>{data.name}</span>
          </h3>
        </Link>

        <ControlCart
          isTablet={isTablet}
          classNameAddButton={styles.root__cart}
          classNameControl={styles.root__control}
          product={data}
          onAddToCart={onAddToCart}
        />

        <Link to={`/product/${data.slug}`} onClick={handleProductClick}>
          <InStock data={data} />
        </Link>
      </div>
    </div>
  )
}
